import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAdmin } from "../../App";
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  LogOut,
  ShieldCheck,
  ArrowLeft,
  Save,
  Plus,
  X,
  ImagePlus,
  FileText,
  Eye,
  EyeOff,
  Star,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

const AdminBlogForm = () => {
  const { articleId } = useParams();
  const isEdit = Boolean(articleId);
  const { admin, adminLogout, adminToken } = useAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "buying_guide",
    author: "",
    author_title: "",
    featured_image: "",
    tags: [],
    read_time: 5,
    meta_title: "",
    meta_description: "",
    meta_keywords: [],
    published: false,
    featured: false,
  });

  const [newTag, setNewTag] = useState("");
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    if (isEdit) {
      fetchArticle();
    }
  }, [articleId, adminToken]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const article = response.data;
      setFormData({
        title: article.title || "",
        excerpt: article.excerpt || "",
        content: article.content || "",
        category: article.category || "buying_guide",
        author: article.author || "",
        author_title: article.author_title || "",
        featured_image: article.featured_image || "",
        tags: article.tags || [],
        read_time: article.read_time || 5,
        meta_title: article.meta_title || "",
        meta_description: article.meta_description || "",
        meta_keywords: article.meta_keywords || [],
        published: article.published || false,
        featured: article.featured || false,
      });
    } catch (error) {
      toast.error("Failed to fetch article");
      navigate("/admin/blog");
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag("");
    }
  };

  const removeTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.meta_keywords.includes(newKeyword.trim())) {
      setFormData((prev) => ({ ...prev, meta_keywords: [...prev.meta_keywords, newKeyword.trim()] }));
      setNewKeyword("");
    }
  };

  const removeKeyword = (index) => {
    setFormData((prev) => ({
      ...prev,
      meta_keywords: prev.meta_keywords.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const required = ["title", "excerpt", "content", "author", "author_title", "featured_image"];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`${field.replace("_", " ")} is required`);
        return false;
      }
    }
    if (formData.title.length < 10) {
      toast.error("Title must be at least 10 characters");
      return false;
    }
    if (formData.excerpt.length < 50) {
      toast.error("Excerpt must be at least 50 characters");
      return false;
    }
    return true;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      const payload = {
        ...formData,
        meta_title: formData.meta_title || formData.title,
        meta_description: formData.meta_description || formData.excerpt.substring(0, 160),
      };

      if (isEdit) {
        await axios.put(`${API}/admin/blog/articles/${articleId}`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast.success("Article updated successfully");
      } else {
        await axios.post(`${API}/admin/blog/articles`, payload, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        toast.success("Article created successfully");
      }
      navigate("/admin/blog");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate("/admin");
  };

  const sidebarItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Building2, label: "Properties", path: "/admin/properties" },
    { icon: FileText, label: "Blog", path: "/admin/blog", active: true },
    { icon: MessageSquare, label: "Inquiries", path: "/admin/inquiries" },
  ];

  const categories = [
    { value: "rera", label: "RERA & Compliance" },
    { value: "buying_guide", label: "Buying Guide" },
    { value: "finance", label: "Home Finance" },
    { value: "market_trends", label: "Market Trends" },
    { value: "legal", label: "Legal & Documentation" },
  ];

  // Calculate estimated read time based on content
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  useEffect(() => {
    if (formData.content) {
      const time = calculateReadTime(formData.content);
      if (time !== formData.read_time) {
        handleInputChange("read_time", time);
      }
    }
  }, [formData.content]);

  if (fetching) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 flex" data-testid="admin-blog-form">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-800 border-r border-stone-700 flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-lg text-white">RERA Admin</div>
              <div className="text-xs text-stone-400">Control Panel</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    item.active
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "text-stone-400 hover:bg-stone-700 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
              {admin?.name?.charAt(0) || "A"}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <p className="text-stone-400 text-xs">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-stone-800 border-b border-stone-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/blog" className="text-stone-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <div>
                <h1 className="font-display text-2xl text-white">
                  {isEdit ? "Edit Article" : "Create New Article"}
                </h1>
                <p className="text-stone-400 text-sm">
                  {isEdit ? "Update article content and SEO settings" : "Write a new blog article with SEO optimization"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setPreviewMode(!previewMode)}
                className="border-stone-600 text-stone-400 hover:bg-stone-700 hover:text-white"
              >
                {previewMode ? <EyeOff className="w-4 h-4 mr-2" strokeWidth={1.5} /> : <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                {previewMode ? "Edit" : "Preview"}
              </Button>
              <Button
                onClick={handleSubmitClick}
                disabled={loading}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="save-article-btn"
              >
                <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {loading ? "Saving..." : isEdit ? "Update Article" : "Publish Article"}
              </Button>
            </div>
          </div>
        </header>

        <div className="p-8">
          {previewMode ? (
            /* Preview Mode */
            <div className="max-w-4xl mx-auto bg-white rounded-sm p-8">
              <div className="mb-6">
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs uppercase tracking-widest rounded-full">
                  {categories.find(c => c.value === formData.category)?.label}
                </span>
              </div>
              <h1 className="font-display text-4xl text-stone-800 mb-4">{formData.title || "Article Title"}</h1>
              <p className="text-xl text-stone-600 mb-6">{formData.excerpt || "Article excerpt..."}</p>
              <div className="flex items-center gap-4 mb-8 text-stone-500">
                <span>{formData.author || "Author Name"}</span>
                <span>•</span>
                <span>{formData.read_time} min read</span>
              </div>
              {formData.featured_image && (
                <img src={formData.featured_image} alt="" className="w-full aspect-[21/9] object-cover rounded mb-8" />
              )}
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.content || "<p>Article content...</p>" }}
              />
              {formData.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t flex items-center gap-2 flex-wrap">
                  <span className="text-stone-500">Tags:</span>
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-stone-100 text-stone-600 text-sm rounded-full">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Edit Mode */
            <form className="space-y-8">
              {/* Basic Info */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Basic Information</h2>
                <div className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Article Title *
                    </label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder="e.g., What is RERA? A Complete Guide for Homebuyers"
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                      data-testid="article-title-input"
                    />
                    <p className="text-xs text-stone-500 mt-1">{formData.title.length} characters</p>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Excerpt / Summary *
                    </label>
                    <Textarea
                      value={formData.excerpt}
                      onChange={(e) => handleInputChange("excerpt", e.target.value)}
                      placeholder="A brief summary of the article (50-200 characters recommended)"
                      rows={3}
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                    <p className="text-xs text-stone-500 mt-1">{formData.excerpt.length} characters (recommended: 50-200)</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                        Category
                      </label>
                      <Select
                        value={formData.category}
                        onValueChange={(v) => handleInputChange("category", v)}
                      >
                        <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                        Read Time (minutes)
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={formData.read_time}
                        onChange={(e) => handleInputChange("read_time", parseInt(e.target.value) || 5)}
                        className="bg-stone-700 border-stone-600 text-white"
                      />
                      <p className="text-xs text-stone-500 mt-1">Auto-calculated from content</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.published}
                        onChange={(e) => handleInputChange("published", e.target.checked)}
                        className="w-5 h-5 rounded border-stone-600 bg-stone-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-stone-300 flex items-center gap-2">
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                        Publish article (visible to public)
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.featured}
                        onChange={(e) => handleInputChange("featured", e.target.checked)}
                        className="w-5 h-5 rounded border-stone-600 bg-stone-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-stone-300 flex items-center gap-2">
                        <Star className="w-4 h-4" strokeWidth={1.5} />
                        Featured article
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Author Info */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Author Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Author Name *
                    </label>
                    <Input
                      value={formData.author}
                      onChange={(e) => handleInputChange("author", e.target.value)}
                      placeholder="e.g., Priya Sharma"
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Author Title *
                    </label>
                    <Input
                      value={formData.author_title}
                      onChange={(e) => handleInputChange("author_title", e.target.value)}
                      placeholder="e.g., Real Estate Legal Expert"
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                  </div>
                </div>
              </div>

              {/* Featured Image */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Featured Image</h2>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    Image URL *
                  </label>
                  <Input
                    value={formData.featured_image}
                    onChange={(e) => handleInputChange("featured_image", e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                  <p className="text-xs text-stone-500 mt-1">Recommended size: 1200x630 pixels for optimal social sharing</p>
                </div>
                {formData.featured_image && (
                  <div className="mt-4">
                    <img
                      src={formData.featured_image}
                      alt="Preview"
                      className="max-w-md h-auto rounded border border-stone-600"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Article Content *</h2>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                    HTML Content
                  </label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    placeholder="<h2>Introduction</h2><p>Your article content here...</p>"
                    rows={20}
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500 font-mono text-sm"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Use HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a&gt;
                  </p>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">Tags</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-emerald-900/30 text-emerald-400 text-sm rounded-full flex items-center gap-2"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(i)}
                        className="text-emerald-600 hover:text-red-400"
                      >
                        <X className="w-3 h-3" strokeWidth={2} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                    placeholder="Add a tag (e.g., RERA, Homebuying)"
                    className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                  />
                  <Button
                    type="button"
                    onClick={addTag}
                    variant="outline"
                    className="border-stone-600 text-stone-400 hover:bg-stone-700"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>

              {/* SEO Settings */}
              <div className="bg-stone-800 border border-stone-700 p-6 rounded-sm">
                <h2 className="font-display text-lg text-white mb-6">SEO Settings</h2>
                <div className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Meta Title
                    </label>
                    <Input
                      value={formData.meta_title}
                      onChange={(e) => handleInputChange("meta_title", e.target.value)}
                      placeholder="Leave empty to use article title"
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                    <p className="text-xs text-stone-500 mt-1">{(formData.meta_title || formData.title).length}/60 characters (recommended max)</p>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Meta Description
                    </label>
                    <Textarea
                      value={formData.meta_description}
                      onChange={(e) => handleInputChange("meta_description", e.target.value)}
                      placeholder="Leave empty to use excerpt"
                      rows={3}
                      className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                    />
                    <p className="text-xs text-stone-500 mt-1">{(formData.meta_description || formData.excerpt).length}/160 characters (recommended max)</p>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-stone-400 mb-2 block">
                      Meta Keywords
                    </label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {formData.meta_keywords.map((keyword, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-blue-900/30 text-blue-400 text-sm rounded-full flex items-center gap-2"
                        >
                          {keyword}
                          <button
                            type="button"
                            onClick={() => removeKeyword(i)}
                            className="text-blue-600 hover:text-red-400"
                          >
                            <X className="w-3 h-3" strokeWidth={2} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                        placeholder="Add SEO keyword"
                        className="bg-stone-700 border-stone-600 text-white placeholder:text-stone-500"
                      />
                      <Button
                        type="button"
                        onClick={addKeyword}
                        variant="outline"
                        className="border-stone-600 text-stone-400 hover:bg-stone-700"
                      >
                        <Plus className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Submit Button */}
              <div className="md:hidden">
                <Button
                  type="button"
                  onClick={handleSubmitClick}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12"
                >
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {loading ? "Saving..." : isEdit ? "Update Article" : "Publish Article"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-stone-800 border-stone-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {isEdit ? "Update Article" : "Publish Article"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Are you sure you want to {isEdit ? "update" : "publish"} this article?
              <br /><br />
              <strong className="text-white">{formData.title}</strong>
              <br />
              <span className="text-stone-500">
                Status: {formData.published ? "Published" : "Draft"}
                {formData.featured && " • Featured"}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-stone-700 text-white hover:bg-stone-600 border-stone-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedSubmit}
              className="bg-amber-500 text-white hover:bg-amber-600"
              data-testid="confirm-save-article-btn"
            >
              {isEdit ? "Update" : "Publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBlogForm;
